import { useEffect } from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions, TextField, Box, Button, Grid, Alert } from '@mui/material';
import { z } from 'zod';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';

const deaneriesSchema = z.object({
  name: z.string().min(1, 'Укажите название'),
  description: z.string().optional(),
  coordinatorId: z.string().optional(),
});

type DeaneriesFormValues = z.infer<typeof deaneriesSchema>;

interface DeaneriesFormProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (data: DeaneriesFormValues) => Promise<void>;
  initialData?: DeaneriesFormValues | null;
  loading: boolean;
  error?: string | null;
}

export function DeaneriesForm({ open, onClose, onSubmit, initialData, loading, error }: DeaneriesFormProps) {
  const { register, handleSubmit, formState: { errors }, reset } = useForm<DeaneriesFormValues>({
    resolver: zodResolver(deaneriesSchema),
    defaultValues: { name: '', description: '', coordinatorId: '' },
  });

  useEffect(() => {
    if (initialData) reset(initialData);
    else reset({ name: '', description: '', coordinatorId: '' });
  }, [initialData, open, reset]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>{initialData ? 'Редактировать благочиние' : 'Добавить благочиние'}</DialogTitle>
      <DialogContent>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        <Box sx={{ mt: 2 }}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField fullWidth label="Название *" error={!!errors.name} helperText={errors.name?.message} {...register('name')} />
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth label="Описание" multiline rows={4} {...register('description')} />
            </Grid>
          </Grid>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Отмена</Button>
        <Button onClick={handleSubmit(onSubmit)} variant="contained" disabled={loading}>{loading ? 'Сохранение...' : 'Сохранить'}</Button>
      </DialogActions>
    </Dialog>
  );
}
